import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Cloud,
  Check,
  Copy,
  Upload,
  Database,
  Key,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Download,
  FileText,
  Globe,
  Lock,
  RefreshCw
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { 
  resourcesData, 
  useUserCloud, 
  EmailCaptureModal,
  EmergentCTA,
  AuthorCard,
  resourceColors as colors 
} from '../ResourceComponents';

const resource = resourcesData['cloudinary-setup'];

// The full prompt that can be copied to Emergent
const FULL_EMERGENT_PROMPT = `# Cloudinary Integration for Persistent Image Storage

## TASK
Integrate Cloudinary for persistent image/video uploads that survive deployments.

## BACKEND SETUP (server.py)

### 1. Install Cloudinary
\`\`\`bash
pip install cloudinary
\`\`\`

### 2. Configure Cloudinary (add to server.py)
\`\`\`python
# Cloudinary configuration
import cloudinary
import cloudinary.uploader
import os
import uuid
import logging

# IMPORTANT: Hardcode your cloud name to prevent typos!
CLOUDINARY_CLOUD = "YOUR_CLOUD_NAME_HERE"  # <-- Replace with your cloud name
CLOUDINARY_KEY = os.environ.get("CLOUDINARY_API_KEY")
CLOUDINARY_SECRET = os.environ.get("CLOUDINARY_API_SECRET")

cloudinary.config(
    cloud_name=CLOUDINARY_CLOUD,
    api_key=CLOUDINARY_KEY,
    api_secret=CLOUDINARY_SECRET
)
\`\`\`

### 3. Upload Endpoint (add to server.py)
\`\`\`python
@api_router.post("/admin/upload")
async def upload_file(file: UploadFile = File(...), admin: AuthUser = Depends(require_admin)):
    """Upload file to Cloudinary - REQUIRES ADMIN AUTH"""
    allowed_types = [
        "image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml",
        "video/mp4", "video/webm", "video/quicktime", "video/x-msvideo"
    ]
    if file.content_type not in allowed_types:
        raise HTTPException(status_code=400, detail="Invalid file type")
    
    try:
        content = await file.read()
        resource_type = "video" if file.content_type.startswith("video/") else "image"
        
        result = cloudinary.uploader.upload(
            content,
            resource_type=resource_type,
            folder="YOUR_PROJECT_FOLDER",  # <-- Replace with your folder name
            public_id=str(uuid.uuid4()),
        )
        
        return {
            "success": True,
            "url": result.get("secure_url"),
            "filename": result.get("public_id"),
            "size": len(content),
            "format": result.get("format")
        }
    except Exception as e:
        logging.error(f"Cloudinary upload failed: {str(e)}")
        raise HTTPException(status_code=500, detail=f"Upload failed: {str(e)}")
\`\`\`

## DEPLOYMENT SECRETS (Emergent)

Add these in Emergent → Deployment Settings → Secrets:
- CLOUDINARY_CLOUD_NAME = your_cloud_name
- CLOUDINARY_API_KEY = your_api_key
- CLOUDINARY_API_SECRET = your_api_secret

## IMPORTANT: Client-Side Image Compression

Deployment proxies often reject uploads over ~1MB. Add client-side compression before uploading:
- Resize to max 1920px width/height
- Compress to 80% JPEG quality
- Use HTML5 Canvas API
This prevents 500 errors on production uploads.

## TESTING CHECKLIST
1. Login as admin
2. Navigate to a page with editable images
3. Upload an image
4. Refresh page - image should persist
5. Open incognito - image should appear for public visitors

## COMMON ISSUES
- Images disappear after deployment → Not using Cloudinary (server files get wiped)
- Upload returns 500 → Image too large, add client-side compression
- Works locally but not production → Add secrets in Emergent deployment settings`;

// Copyable Code Block Component
const CopyablePrompt = ({ title, prompt, language = 'python' }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(prompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-semibold text-gray-900 text-sm">{title}</h4>
        <button 
          onClick={handleCopy}
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
        >
          {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <pre className="bg-gray-900 text-gray-100 rounded-xl p-4 overflow-x-auto text-sm whitespace-pre-wrap">
        <code>{prompt}</code>
      </pre>
    </div>
  );
};

// Copy Full Prompt Button
const CopyFullPromptButton = () => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(FULL_EMERGENT_PROMPT);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };
  
  return (
    <button 
      onClick={handleCopy}
      className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
        copied 
          ? 'bg-green-500 text-white' 
          : 'bg-gradient-to-r from-green-500 to-teal-500 text-white hover:from-green-600 hover:to-teal-600'
      }`}
    >
      {copied ? (
        <>
          <Check className="w-5 h-5" />
          Copied to Clipboard!
        </>
      ) : (
        <>
          <Copy className="w-5 h-5" />
          Copy Full Prompt for Emergent
        </>
      )}
    </button>
  );
};

// Download as Text Button
const DownloadButton = () => {
  const handleDownload = () => {
    const blob = new Blob([FULL_EMERGENT_PROMPT], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'cloudinary-setup-guide.md';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  return (
    <button 
      onClick={handleDownload}
      className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
    >
      <Download className="w-5 h-5" />
      Download as .md File
    </button>
  );
};

const CloudinarySetupPage = () => {
  const [showFullPrompt, setShowFullPrompt] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-50 to-teal-50 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Link to="/resources" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Resources
          </Link>
          
          <div className="flex items-start gap-4 mb-6">
            <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center flex-shrink-0">
              <Cloud className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-1 rounded-lg bg-green-100 text-green-700 text-xs font-medium">Integration</span>
                <span className="px-2 py-1 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium">Production-Ready</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Cloudinary Setup Guide</h1>
              <p className="text-gray-600">
                Complete, production-tested guide to integrate Cloudinary for persistent image storage.
              </p>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <CopyFullPromptButton />
            <DownloadButton />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        
        {/* Quick Copy Section */}
        <section className="mb-12">
          <div className="p-6 rounded-2xl bg-gradient-to-r from-green-50 to-teal-50 border-2 border-green-200">
            <div className="flex items-start gap-3 mb-4">
              <FileText className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Quick Start: Copy the Full Prompt</h2>
                <p className="text-gray-600 mb-4">
                  Click the button above to copy the complete Cloudinary integration prompt. 
                  Paste it directly into Emergent or any AI coding assistant.
                </p>
                <button 
                  onClick={() => setShowFullPrompt(!showFullPrompt)}
                  className="text-green-600 hover:text-green-700 font-medium text-sm"
                >
                  {showFullPrompt ? '▼ Hide Full Prompt' : '▶ Preview Full Prompt'}
                </button>
              </div>
            </div>
            
            {showFullPrompt && (
              <pre className="mt-4 bg-gray-900 text-gray-100 rounded-xl p-4 overflow-x-auto text-xs max-h-96 overflow-y-auto whitespace-pre-wrap">
                {FULL_EMERGENT_PROMPT}
              </pre>
            )}
          </div>
        </section>

        {/* Why Cloudinary */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Why You Need This</h2>
          <div className="p-4 rounded-xl bg-red-50 border border-red-200 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-red-800 font-medium">Without cloud storage, uploaded images are wiped on every deployment!</p>
                <p className="text-sm text-red-700 mt-1">Files saved to the server's local filesystem don't persist. Cloudinary solves this.</p>
              </div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 rounded-xl bg-green-50 border border-green-200">
              <CheckCircle className="w-6 h-6 text-green-600 mb-2" />
              <h4 className="font-semibold text-gray-900 mb-1">25GB Free</h4>
              <p className="text-sm text-gray-600">Generous free tier with CDN</p>
            </div>
            <div className="p-4 rounded-xl bg-green-50 border border-green-200">
              <CheckCircle className="w-6 h-6 text-green-600 mb-2" />
              <h4 className="font-semibold text-gray-900 mb-1">Permanent URLs</h4>
              <p className="text-sm text-gray-600">Images survive deployments</p>
            </div>
            <div className="p-4 rounded-xl bg-green-50 border border-green-200">
              <CheckCircle className="w-6 h-6 text-green-600 mb-2" />
              <h4 className="font-semibold text-gray-900 mb-1">Auto Optimization</h4>
              <p className="text-sm text-gray-600">Images optimized automatically</p>
            </div>
          </div>
        </section>

        {/* Step by Step */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Step-by-Step Guide</h2>
          
          {/* Step 1 */}
          <div className="mb-8 p-6 rounded-2xl border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center font-bold text-green-700">1</div>
              <h3 className="text-lg font-bold text-gray-900">Get Cloudinary Credentials</h3>
            </div>
            <ol className="list-decimal list-inside space-y-2 text-gray-600">
              <li>Sign up at <a href="https://cloudinary.com" target="_blank" rel="noopener noreferrer" className="text-green-600 underline">cloudinary.com</a></li>
              <li>Go to <strong>Dashboard</strong></li>
              <li>Copy: <strong>Cloud Name</strong>, <strong>API Key</strong>, <strong>API Secret</strong></li>
            </ol>
          </div>

          {/* Step 2 */}
          <div className="mb-8 p-6 rounded-2xl border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center font-bold text-green-700">2</div>
              <h3 className="text-lg font-bold text-gray-900">Backend Configuration</h3>
            </div>
            
            <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200 mb-4">
              <p className="text-sm text-yellow-800">
                <strong>⚠️ HARDCODE your cloud name!</strong> Environment variables can have typos that cause hours of debugging.
              </p>
            </div>
            
            <CopyablePrompt 
              title="Cloudinary Config (server.py)"
              prompt={`# Cloudinary configuration
import cloudinary
import cloudinary.uploader
import os
import uuid

# HARDCODE your cloud name!
CLOUDINARY_CLOUD = "YOUR_CLOUD_NAME"  # <-- Replace
CLOUDINARY_KEY = os.environ.get("CLOUDINARY_API_KEY")
CLOUDINARY_SECRET = os.environ.get("CLOUDINARY_API_SECRET")

cloudinary.config(
    cloud_name=CLOUDINARY_CLOUD,
    api_key=CLOUDINARY_KEY,
    api_secret=CLOUDINARY_SECRET
)`}
            />
          </div>

          {/* Step 3 */}
          <div className="mb-8 p-6 rounded-2xl border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center font-bold text-green-700">3</div>
              <h3 className="text-lg font-bold text-gray-900">Upload Endpoint</h3>
            </div>
            
            <CopyablePrompt 
              title="Upload Endpoint (server.py)"
              prompt={`@api_router.post("/admin/upload")
async def upload_file(file: UploadFile = File(...), admin = Depends(require_admin)):
    allowed_types = ["image/jpeg", "image/png", "image/gif", "image/webp", "video/mp4"]
    if file.content_type not in allowed_types:
        raise HTTPException(status_code=400, detail="Invalid file type")
    
    content = await file.read()
    resource_type = "video" if file.content_type.startswith("video/") else "image"
    
    result = cloudinary.uploader.upload(
        content,
        resource_type=resource_type,
        folder="YOUR_FOLDER",  # <-- Replace
        public_id=str(uuid.uuid4()),
    )
    
    return {"success": True, "url": result.get("secure_url")}`}
            />
          </div>

          {/* Step 4 */}
          <div className="mb-8 p-6 rounded-2xl border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center font-bold text-green-700">4</div>
              <h3 className="text-lg font-bold text-gray-900">Deployment Secrets</h3>
            </div>
            
            <p className="text-gray-600 mb-4">Add in Emergent → Deployment Settings → Secrets:</p>
            
            <div className="bg-gray-900 text-gray-100 rounded-xl p-4 font-mono text-sm">
              <p className="text-green-400"># Add these as deployment secrets</p>
              <p className="mt-2">CLOUDINARY_CLOUD_NAME = your_cloud_name</p>
              <p>CLOUDINARY_API_KEY = your_api_key</p>
              <p>CLOUDINARY_API_SECRET = your_api_secret</p>
            </div>
            
            <p className="text-sm text-gray-500 mt-3">
              <strong>Tip:</strong> Client-side image compression (max 1920px, 80% JPEG) prevents 500 errors from deployment proxy size limits.
            </p>
          </div>
        </section>

        {/* Common Issues */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Common Issues</h2>
          <div className="space-y-3">
            <div className="p-4 rounded-xl border border-gray-200">
              <p className="font-medium text-gray-900">Images disappear after deployment</p>
              <p className="text-sm text-gray-600">Images must be stored on Cloudinary, not the server filesystem. Server files get wiped on every deploy.</p>
            </div>
            <div className="p-4 rounded-xl border border-gray-200">
              <p className="font-medium text-gray-900">Upload returns 500 error</p>
              <p className="text-sm text-gray-600">Image too large for deployment proxy. Add client-side compression (max 1920px, 80% JPEG quality) before uploading.</p>
            </div>
            <div className="p-4 rounded-xl border border-gray-200">
              <p className="font-medium text-gray-900">Works locally but not in production</p>
              <p className="text-sm text-gray-600">Add CLOUDINARY_CLOUD_NAME, CLOUDINARY_API_KEY, and CLOUDINARY_API_SECRET as deployment secrets in Emergent.</p>
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="mb-12">
          <div className="p-6 rounded-2xl bg-gradient-to-r from-green-100 to-teal-100 border border-green-200 text-center">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Ready to integrate?</h3>
            <div className="flex flex-wrap justify-center gap-3">
              <CopyFullPromptButton />
              <DownloadButton />
            </div>
          </div>
        </section>

        {/* Author */}
        <AuthorCard author={resource?.author || {
          name: 'techbaenae',
          handle: '@techbaenae',
          avatar: 'https://customer-assets.emergentagent.com/job_e02576fc-3e7a-4446-bc21-d558624baf1e/artifacts/zhpzfvij_naomi%20headshot%20zoomed%20in.jpg',
          title: 'Data Dreamer'
        }} />

        {/* CTA */}
        <EmergentCTA />
      </div>
    </div>
  );
};

export default CloudinarySetupPage;
